import React, { useState, useEffect } from 'react';

const emptySeminar = {
  id: '',
  label: '',
  number: '',
  eventDate: '',
  date: '',
  time: '19:00〜20:10',
  shortTitle: '',
  fullTitle: '',
  fullDate: '',
  badge: '毎月開催',
  cardBadge: '',
  intro: '',
  bullets: ['', '', ''],
  special: false,
};

const SeminarAdmin = () => {
  const [seminars, setSeminars] = useState([]);
  const [editing, setEditing] = useState(null); // index or 'new'
  const [form, setForm] = useState(emptySeminar);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch('/data/seminars.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setSeminars)
      .catch(() => setSeminars([]));
  }, []);

  const startNew = () => {
    setForm({ ...emptySeminar, id: Date.now() });
    setEditing('new');
    setMessage('');
  };

  const startEdit = (index) => {
    const s = seminars[index];
    setForm({ ...s, bullets: [...(s.bullets || ['', '', ''])] });
    setEditing(index);
    setMessage('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptySeminar);
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateBullet = (i, value) => {
    setForm((prev) => {
      const bullets = [...prev.bullets];
      bullets[i] = value;
      return { ...prev, bullets };
    });
  };

  const addBullet = () => {
    setForm((prev) => ({ ...prev, bullets: [...prev.bullets, ''] }));
  };

  const removeBullet = (i) => {
    setForm((prev) => ({
      ...prev,
      bullets: prev.bullets.filter((_, idx) => idx !== i),
    }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');

    const cleaned = { ...form, bullets: form.bullets.filter((b) => b.trim() !== '') };
    let updated;
    if (editing === 'new') {
      updated = [...seminars, cleaned];
    } else {
      updated = seminars.map((s, i) => (i === editing ? cleaned : s));
    }

    try {
      const res = await fetch('/api/admin-seminars.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', seminars: updated }),
      });
      const data = await res.json();
      if (data.success) {
        setSeminars(updated);
        setEditing(null);
        setForm(emptySeminar);
        setMessage('保存しました');
      } else {
        setMessage('保存に失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (index) => {
    if (!window.confirm('このセミナーを削除しますか？')) return;
    const updated = seminars.filter((_, i) => i !== index);
    try {
      const res = await fetch('/api/admin-seminars.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', seminars: updated }),
      });
      const data = await res.json();
      if (data.success) {
        setSeminars(updated);
        setMessage('削除しました');
      }
    } catch {
      setMessage('削除に失敗しました');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">セミナー管理</h2>
        {editing === null && (
          <button
            onClick={startNew}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors"
          >
            + 新規追加
          </button>
        )}
      </div>

      {message && (
        <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">
          {message}
        </div>
      )}

      {/* 編集フォーム */}
      {editing !== null && (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h3 className="font-bold text-lg mb-4">
            {editing === 'new' ? '新規セミナー' : 'セミナー編集'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="番号 (例: 第1回)" value={form.number} onChange={(v) => updateField('number', v)} />
            <Field label="ラベル (例: 第1回 3/27)" value={form.label} onChange={(v) => updateField('label', v)} />
            <div>
              <label className="block text-sm font-bold mb-1">開催日（自動非表示の判定用）</label>
              <input
                type="date"
                value={form.eventDate || ''}
                onChange={(e) => updateField('eventDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <Field label="日付表示 (例: 3月27日（木）)" value={form.date} onChange={(v) => updateField('date', v)} />
            <Field label="時間 (例: 19:00〜20:10)" value={form.time} onChange={(v) => updateField('time', v)} />
            <Field label="短いタイトル" value={form.shortTitle} onChange={(v) => updateField('shortTitle', v)} />
            <Field label="フルタイトル" value={form.fullTitle} onChange={(v) => updateField('fullTitle', v)} />
            <Field label="フル日付 (例: 第1回　3月27日（木）19:00〜20:10)" value={form.fullDate} onChange={(v) => updateField('fullDate', v)} />
            <Field label="バッジ" value={form.badge} onChange={(v) => updateField('badge', v)} />
            <Field label="カードバッジ" value={form.cardBadge} onChange={(v) => updateField('cardBadge', v)} />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.special}
                onChange={(e) => updateField('special', e.target.checked)}
                className="w-4 h-4"
              />
              <label className="text-sm font-bold">特別回</label>
            </div>
          </div>

          <div className="mt-4">
            <Field
              label="紹介文"
              value={form.intro}
              onChange={(v) => updateField('intro', v)}
              textarea
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-bold mb-2">ポイント（箇条書き）</label>
            {form.bullets.map((b, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={b}
                  onChange={(e) => updateBullet(i, e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button onClick={() => removeBullet(i)} className="text-red-500 text-sm px-2 hover:text-red-700">✕</button>
              </div>
            ))}
            <button onClick={addBullet} className="text-blue-600 text-sm font-bold hover:underline">
              + 項目追加
            </button>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={save}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? '保存中...' : '保存'}
            </button>
            <button
              onClick={cancel}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300 transition-colors"
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* 一覧 */}
      <div className="space-y-3">
        {seminars.length === 0 && editing === null && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
            セミナーがありません。「新規追加」で作成してください。
          </div>
        )}
        {seminars.map((s, i) => (
          <div key={s.id || i} className="bg-white rounded-xl shadow p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {s.special && <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded">特別回</span>}
                <span className="text-sm font-bold text-gray-500">{s.number}</span>
                <span className="text-sm text-gray-400">{s.date} {s.time}</span>
              </div>
              <p className="font-bold">{s.shortTitle || s.fullTitle}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => startEdit(i)}
                className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-gray-200 transition-colors"
              >
                編集
              </button>
              <button
                onClick={() => remove(i)}
                className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-red-100 transition-colors"
              >
                削除
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, textarea }) => (
  <div>
    <label className="block text-sm font-bold mb-1">{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    )}
  </div>
);

export default SeminarAdmin;
